import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';

const API_URL = '/customers';
const SHOW_VALIDATE_CUIT = true;

const CustomerFormModal = ({ isOpen, onClose, onSuccess, initialData = null, isEcommerceLite = false }) => {
    const [formData, setFormData] = useState({
        _id: '',
        razonSocial: '',
        cuit: '',
        tipoContribuyente: '',
        ingresosBrutos: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
        cuentaCorrienteEnabled: false,
        creditLimit: 0,
        paymentDueDays: 0,
    });
    const [validateLoading, setValidateLoading] = useState(false);
    const editMode = !!initialData;

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setFormData({
                    _id: initialData._id,
                    razonSocial: initialData.razonSocial || initialData.name || '',
                    cuit: initialData.cuit || '',
                    ingresosBrutos: (initialData.ingresosBrutos && initialData.ingresosBrutos.numero) || initialData.ingresosBrutos || '',
                    tipoContribuyente: initialData.tipoContribuyente || '',
                    phone: initialData.phone || '',
                    email: initialData.email || '',
                    address: initialData.address || '',
                    notes: initialData.notes || '',
                    cuentaCorrienteEnabled: initialData.cuentaCorrienteEnabled || false,
                    creditLimit: initialData.creditLimit || 0,
                    paymentDueDays: initialData.paymentDueDays || 0,
                });
            } else {
                // Reset full
                setFormData({
                    _id: '', razonSocial: '', cuit: '', tipoContribuyente: '',
                    ingresosBrutos: '', phone: '', email: '', address: '', notes: '',
                    cuentaCorrienteEnabled: false, creditLimit: 0, paymentDueDays: 0
                });
            }
        }
    }, [isOpen, initialData]);

    const handleValidateCuit = async () => {
        try {
            if (!formData.cuit || String(formData.cuit).trim() === '') {
                toast.warning('⚠️ Ingrese un CUIT para validar');
                return;
            }
            setValidateLoading(true);
            const cuitRaw = String(formData.cuit).trim();

            try {
                const resp = await httpRequest.get(`${API_URL}/validate-afip/${encodeURIComponent(cuitRaw)}`);
                if (resp.data?.ok) {
                    const remote = resp.data.data || {};
                    setFormData((prev) => ({
                        ...prev,
                        razonSocial: remote.razonSocial || remote.nombre || prev.razonSocial,
                        tipoContribuyente: remote.tipoContribuyente || remote.condicionIVA || prev.tipoContribuyente,
                    }));
                    toast.success('✅ CUIT validado con padrón AFIP oficial.');
                    return;
                }
            } catch (afipErr) {
                // Fallback ARCA
                try {
                    const respArca = await httpRequest.get(`${API_URL}/validate/${encodeURIComponent(cuitRaw)}`);
                    if (respArca.data?.ok) {
                        const remote = respArca.data.data || {};
                        setFormData((prev) => ({
                            ...prev,
                            razonSocial: remote.razonSocial || remote.nombre || prev.razonSocial,
                            tipoContribuyente: remote.tipoContribuyente || remote.condicionIVA || prev.tipoContribuyente,
                            ingresosBrutos: (remote.ingresosBrutos?.numero || remote.ingresosBrutos) || prev.ingresosBrutos
                        }));
                        toast.success('✅ CUIT validado con servicio alternativo.');
                    } else {
                        toast.warning('No se encontraron datos para ese CUIT (servicio alternativo).');
                    }
                } catch (e) {
                    toast.error('❌ Error validando CUIT en ambos servicios.');
                }
            }
        } catch (e) {
            console.error(e);
            toast.error('Error general validando CUIT');
        } finally {
            setValidateLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.razonSocial || !formData.cuit) {
            toast.warning('⚠️ Razón social y CUIT son obligatorios');
            return;
        }

        try {
            let result;
            if (editMode && formData._id) {
                const res = await httpRequest.patch(`${API_URL}/update/${formData._id}`, formData);
                result = res.data;
                toast.success('✅ Cliente actualizado');
            } else {
                const { _id, ...dataToSend } = formData;
                const res = await httpRequest.post(`${API_URL}/create`, dataToSend);
                result = res.data;
                toast.success('✅ Cliente creado');
            }

            if (onSuccess) onSuccess(result);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || '❌ Error al guardar cliente');
        }
    };

    if (!isOpen) return null;

    return (
        <div style={modalOverlay} onClick={onClose}>
            <div style={modalContent} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ marginBottom: '20px' }}>
                    {editMode ? '✏️ Editar Cliente' : '➕ Nuevo Cliente'}
                </h2>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>Razón Social *</label>
                        <input
                            type="text"
                            value={formData.razonSocial}
                            onChange={(e) => setFormData({ ...formData, razonSocial: e.target.value })}
                            style={inputModal}
                            placeholder="Razón social / nombre de la empresa"
                            required
                        />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>CUIT (solo números) *</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                                type="text"
                                value={formData.cuit}
                                onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                                style={{ ...inputModal, flex: 1 }}
                                placeholder="20123456789"
                                required
                            />
                            {SHOW_VALIDATE_CUIT && (
                                <button
                                    type="button"
                                    onClick={handleValidateCuit}
                                    disabled={validateLoading}
                                    style={{
                                        padding: '8px 10px', borderRadius: '6px', border: 'none',
                                        background: validateLoading ? '#6c757d' : '#17a2b8', color: '#fff',
                                        cursor: validateLoading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {validateLoading ? '⏳' : '🔍 Validar'}
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>IIBB</label>
                        <input
                            type="text"
                            value={formData.ingresosBrutos}
                            onChange={(e) => setFormData({ ...formData, ingresosBrutos: e.target.value })}
                            style={inputModal}
                            placeholder="Nro IIBB"
                        />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>Tipo de Contribuyente</label>
                        <select
                            value={formData.tipoContribuyente}
                            onChange={(e) => setFormData({ ...formData, tipoContribuyente: e.target.value })}
                            style={inputModal}
                        >
                            <option value="">-- Seleccionar --</option>
                            <option value="IVA Responsable Inscripto">IVA Responsable Inscripto</option>
                            <option value="IVA Responsable No Inscripto">IVA Responsable No Inscripto</option>
                            <option value="IVA Exento">IVA Exento</option>
                            <option value="Consumidor Final">Consumidor Final</option>
                            <option value="Responsable Monotributo">Responsable Monotributo</option>
                            <option value="Sujeto No Categorizado">Sujeto No Categorizado</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={labelStyle}>Email</label>
                            <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={inputModal} />
                        </div>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={labelStyle}>Teléfono</label>
                            <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} style={inputModal} />
                        </div>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={labelStyle}>Dirección</label>
                        <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={inputModal} />
                    </div>

                    {/* Cuenta Corriente */}
                    {!isEcommerceLite && (
                        <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '15px', marginTop: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '10px' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.cuentaCorrienteEnabled}
                                    onChange={(e) => setFormData({ ...formData, cuentaCorrienteEnabled: e.target.checked })}
                                />
                                <span style={{ fontWeight: 'bold' }}>Habilitar Cuenta Corriente</span>
                            </label>

                            {formData.cuentaCorrienteEnabled && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div>
                                        <label style={labelStyle}>Límite ($)</label>
                                        <input type="number" value={formData.creditLimit} onChange={(e) => setFormData({ ...formData, creditLimit: parseFloat(e.target.value) || 0 })} style={inputModal} />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Plazo (días)</label>
                                        <input type="number" value={formData.paymentDueDays} onChange={(e) => setFormData({ ...formData, paymentDueDays: parseInt(e.target.value) || 0 })} style={inputModal} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button type="button" style={{ ...btnStyle, background: '#6c757d' }} onClick={onClose}>Cancelar</button>
                        <button type="submit" style={{ ...btnStyle, background: '#28a745' }}>{editMode ? 'Guardar Cambios' : 'Crear Cliente'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Styles reused
const modalOverlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
    display: 'flex', justifyContent: 'center', alignItems: 'center'
};
const modalContent = {
    backgroundColor: 'white', padding: '25px', borderRadius: '8px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)', width: '90%', maxWidth: '500px',
    maxHeight: '90vh', overflowY: 'auto'
};
const labelStyle = { fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '4px' };
const inputModal = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' };
const btnStyle = { border: 'none', padding: '8px 16px', borderRadius: '4px', color: 'white', cursor: 'pointer', fontWeight: 'bold' };

export default CustomerFormModal;
